import React from 'react';
import PropTypes from 'prop-types';
import {
  FaFire,
  FaSlidersH,
  FaDesktop,
  FaTabletAlt,
  FaMobileAlt,
  FaMousePointer,
  FaChevronDown,
  FaCalendarAlt
} from 'react-icons/fa';
import {
  formatDisplayDate,
  getDaysAgoIsoDate,
  getTodayIsoDate
} from '../../../analytics/endUser/shared/heatmapViewUtils';

export default function HeatmapSkeleton() {
  const fromDisplay = formatDisplayDate(getDaysAgoIsoDate(7));
  const toDisplay = formatDisplayDate(getTodayIsoDate());
  return (
    <div
      className="skeleton-wireframe w-full flex flex-col gap-5 text-slate-800"
      aria-hidden="true"
      data-testid="heatmap-skeleton"
    >
      {/* 1. Header */}
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Heatmap Analytics</h1>
        <p className="text-xs text-slate-500">
          Analyze user click density, scroll depth, and interaction patterns across portal pages.
        </p>
      </div>

      {/* 2. Navigation Tabs */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          tabIndex={-1}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-semibold shadow-sm"
        >
          <FaFire className="text-orange-300" /> Heatmap Viewer
        </button>
        <button
          type="button"
          tabIndex={-1}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 shadow-sm"
        >
          <FaMousePointer className="text-slate-500" /> Click Density
        </button>
        <button
          type="button"
          tabIndex={-1}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 shadow-sm"
        >
          <FaSlidersH className="text-slate-500" /> Page Configuration
        </button>
      </div>

      {/* 3. Filter & Viewport Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center justify-between gap-4 shadow-sm text-xs">
        <div className="flex flex-wrap items-center gap-4">
          {/* Target Page Selector */}
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Target Page</span>
            <div className="flex items-center justify-between gap-3 bg-white border border-slate-300 rounded-md px-3 py-1.5 min-w-[220px] text-slate-800 font-medium">
              <span>Homepage / Landing (/portal/home)</span>
              <FaChevronDown className="text-[9px] text-slate-400" />
            </div>
          </div>

          {/* From Date */}
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">From</span>
            <div className="flex items-center justify-between gap-2 bg-white border border-slate-300 rounded-md px-3 py-1.5 min-w-[120px] text-slate-700">
              <span>{fromDisplay}</span>
              <FaCalendarAlt className="text-slate-400 text-[11px]" />
            </div>
          </div>

          {/* To Date */}
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">To</span>
            <div className="flex items-center justify-between gap-2 bg-white border border-slate-300 rounded-md px-3 py-1.5 min-w-[120px] text-slate-700">
              <span>{toDisplay}</span>
              <FaCalendarAlt className="text-slate-400 text-[11px]" />
            </div>
          </div>
        </div>

        {/* Viewport Device Switcher */}
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-indigo-600 font-bold rounded-md shadow-xs text-xs">
            <FaDesktop /> Desktop
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-slate-600 font-medium text-xs">
            <FaTabletAlt /> Tablet
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-slate-600 font-medium text-xs">
            <FaMobileAlt /> Mobile
          </span>
        </div>
      </div>

      {/* 4. Thermal Heatmap Canvas Preview */}
      <div className="relative bg-slate-50 border border-slate-200 rounded-2xl p-6 min-h-[460px] overflow-hidden shadow-sm">
        {/* Mock Portal Page Layout Wireframe */}
        <div className="w-full bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
          {/* Mock Top Nav */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-slate-100 bg-slate-50/80">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded bg-indigo-600" />
              <div className="w-24 h-3 bg-slate-300 rounded" />
            </div>
            <div className="flex items-center gap-4">
              <div className="w-16 h-2.5 bg-slate-200 rounded" />
              <div className="w-16 h-2.5 bg-slate-200 rounded" />
              <div className="w-20 h-7 bg-indigo-500 rounded-md" />
            </div>
          </div>

          {/* Mock Hero Section */}
          <div className="p-8 flex flex-col items-center text-center gap-4 bg-gradient-to-b from-indigo-50/30 to-white">
            <div className="w-2/3 h-6 bg-slate-700 rounded-md" />
            <div className="w-1/2 h-3.5 bg-slate-300 rounded" />
            <div className="flex gap-3 mt-2">
              <div className="w-32 h-9 bg-indigo-600 rounded-lg shadow-sm" />
              <div className="w-28 h-9 bg-slate-200 rounded-lg" />
            </div>
          </div>

          {/* Mock Feature Cards Grid */}
          <div className="grid grid-cols-3 gap-4 p-6 border-t border-slate-100 bg-white">
            {[1, 2, 3].map((card) => (
              <div key={card} className="p-4 border border-slate-100 rounded-lg flex flex-col gap-2">
                <div className="w-8 h-8 rounded bg-slate-200 mb-1" />
                <div className="w-3/4 h-3.5 bg-slate-600 rounded" />
                <div className="w-full h-2.5 bg-slate-200 rounded" />
                <div className="w-2/3 h-2.5 bg-slate-200 rounded" />
              </div>
            ))}
          </div>
        </div>

        {/* 5. Thermal Hotspot Glow Overlays */}
        {/* Hotspot 1: High Intensity (Red/Orange/Yellow) over primary CTA */}
        <div
          className="absolute pointer-events-none rounded-full"
          style={{
            top: '190px',
            left: 'calc(50% - 90px)',
            width: '130px',
            height: '80px',
            background: 'radial-gradient(ellipse at center, rgba(239, 68, 68, 0.85) 0%, rgba(245, 158, 11, 0.7) 40%, rgba(234, 179, 8, 0.4) 65%, transparent 80%)',
            filter: 'blur(10px)'
          }}
        />

        {/* Hotspot 2: Medium Intensity (Yellow/Green) over header login CTA */}
        <div
          className="absolute pointer-events-none rounded-full"
          style={{
            top: '32px',
            right: '48px',
            width: '90px',
            height: '50px',
            background: 'radial-gradient(ellipse at center, rgba(234, 179, 8, 0.8) 0%, rgba(34, 197, 94, 0.5) 50%, transparent 75%)',
            filter: 'blur(8px)'
          }}
        />

        {/* Hotspot 3: Moderate Intensity over 1st card */}
        <div
          className="absolute pointer-events-none rounded-full"
          style={{
            bottom: '70px',
            left: '120px',
            width: '110px',
            height: '70px',
            background: 'radial-gradient(ellipse at center, rgba(245, 158, 11, 0.75) 0%, rgba(59, 130, 246, 0.4) 55%, transparent 75%)',
            filter: 'blur(9px)'
          }}
        />

        {/* 6. Floating Heatmap Legend */}
        <div className="absolute bottom-6 right-6 bg-white/90 backdrop-blur-md border border-slate-200 rounded-xl px-4 py-2.5 flex items-center gap-3 shadow-md text-xs">
          <span className="font-bold text-slate-600 text-[10px] uppercase">Click Density:</span>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-slate-400">Low</span>
            <div
              className="w-24 h-2.5 rounded-full"
              style={{
                background: 'linear-gradient(to right, #3b82f6, #06b6d4, #22c55e, #eab308, #ef4444)'
              }}
            />
            <span className="text-[10px] text-slate-700 font-bold">High</span>
          </div>
          <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 font-bold rounded text-[10px]">
            1,480 Clicks
          </span>
        </div>
      </div>
    </div>
  );
}

HeatmapSkeleton.propTypes = {
  featureName: PropTypes.string
};
